import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Auth, Users } from "@/lib/api";

const AuthCtx = createContext({
  user: null,
  loading: true,
  users: [],
  refreshUsers: () => {},
  logout: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);

  const refreshUsers = useCallback(async () => {
    try {
      const list = await Users.list();
      setUsers(list);
    } catch {}
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const me = await Auth.me();
      setUser(me);
      await refreshUsers();
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [refreshUsers]);

  useEffect(() => {
    // Microsoft OAuth is fully server-side (backend sets cookie, redirects to /).
    // No hash fragment handling needed here — /me will return the user if the cookie is valid.
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try { await Auth.logout(); } catch {}
    setUser(null);
    window.location.href = "/login";
  };

  const setUserExternal = async (u) => {
    setUser(u);
    await refreshUsers();
    setLoading(false);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, users, refreshUsers, logout, setUserExternal }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
// Backwards-compat alias
export const useTeam = () => {
  const { users, user, refreshUsers } = useAuth();
  return { users, currentUser: user, currentUserId: user?.id || "", setCurrentUserId: () => {}, refresh: refreshUsers };
};
